import { formatDisplayEmail } from '@bs/core'
import { pantryColors } from '@pantry/ui'
import type { KeyboardEvent } from 'react'
import React, { useState } from 'react'
import { useDebounce } from '../../../../hooks/useDebounce'
import { useCouponCustomerSearch } from '../hooks/useCouponCustomerSearch'
import { isValidEmailFormat } from '../utils/email'
import { CouponCustomerSearchDropdown } from './CouponCustomerSearchDropdown'

const p = pantryColors

const inputStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  padding: '9px 12px',
  border: `1px solid ${p.gray[200]}`,
  borderRadius: 6,
  fontSize: 13,
  color: p.gray[800],
  background: p.white,
  outline: 'none',
}

const subLabel: React.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 600,
  color: p.gray[500],
  marginBottom: 8,
}

interface Props {
  emails: string[]
  onChange: (next: string[]) => void
}

/**
 * "Specific customers" picker for the coupon modal's Customer eligibility
 * section. Searches existing customers (Cova / Greenline) by name / email and
 * also accepts a free-typed email not in the results — mirroring the legacy
 * per-POS coupon customer autocomplete. Emails are the only stored value; the
 * chip UX matches the Settings > Ecommerce "Email notifications" input.
 */
export const CouponCustomerPicker: React.FC<Props> = ({ emails, onChange }) => {
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const debouncedQuery = useDebounce(query, 250)
  const { results, isLoading, searchSupported } = useCouponCustomerSearch(
    debouncedQuery,
    { excludeEmails: emails },
  )
  const visibleResults = results.slice(0, 8)

  const trimmed = query.trim()
  const alreadyPicked = (email: string) =>
    emails.some((e) => e.toLowerCase() === email.toLowerCase())
  // Show the synthetic "+ Add" row when the typed text is a valid email that
  // isn't already picked and isn't one of the customer results (which already
  // render their own row). Mirrors the legacy `filterOptions` `isAdd` behavior.
  const canAddTyped =
    trimmed !== '' &&
    isValidEmailFormat(trimmed) &&
    !alreadyPicked(trimmed) &&
    !results.some((r) => r.email.toLowerCase() === trimmed.toLowerCase())

  const addEmail = (email: string) => {
    if (!alreadyPicked(email)) onChange([...emails, email])
    setQuery('')
    setError(null)
  }

  const removeEmail = (email: string) => {
    onChange(emails.filter((e) => e !== email))
  }

  const commitTyped = () => {
    if (trimmed === '') return
    if (!isValidEmailFormat(trimmed)) {
      setError('Enter a valid email address.')
      return
    }
    addEmail(trimmed)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commitTyped()
    } else if (e.key === 'Backspace' && query === '' && emails.length > 0) {
      onChange(emails.slice(0, -1))
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={subLabel}>
        {searchSupported
          ? 'Search customers or type an email'
          : 'Type an email address'}
      </div>
      <input
        style={{
          ...inputStyle,
          borderColor: error ? p.red[700] : p.gray[200],
        }}
        type="text"
        placeholder={
          searchSupported
            ? 'Search name or email, then Enter to add…'
            : 'Add email address…'
        }
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          if (error) setError(null)
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {query && (searchSupported || canAddTyped) && (
        <CouponCustomerSearchDropdown
          visibleResults={visibleResults}
          isLoading={isLoading}
          searchSupported={searchSupported}
          canAddTyped={canAddTyped}
          query={query}
          trimmed={trimmed}
          onAdd={addEmail}
        />
      )}

      {error ? (
        <span
          style={{
            display: 'block',
            fontSize: 12,
            color: p.red[700],
            marginTop: 4,
          }}
        >
          {error}
        </span>
      ) : null}

      {emails.length > 0 && (
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}
        >
          {emails.map((email) => (
            <span
              key={email}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                background: p.brand.light,
                color: p.brand.primary,
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {formatDisplayEmail(email)}
              <button
                type="button"
                onClick={() => removeEmail(email)}
                aria-label={`Remove ${formatDisplayEmail(email)}`}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: 'inherit',
                  fontSize: 14,
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
